import React, { useState } from 'react';
import { signUp } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';
import { ForgotPassword } from './ForgotPassword';

export function AuthPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signIn, loading: authLoading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      if (!email.trim()) {
        setError('Please enter your email address');
        setLoading(false);
        return;
      }
      if (!password.trim()) {
        setError('Please enter your password');
        setLoading(false);
        return;
      }
      if (isSignUp && password.length < 6) {
        setError('Password must be at least 6 characters long');
        setLoading(false);
        return;
      }

      if (isSignUp) {
        const userData = {
          full_name: '',
          has_partner: false,
          partner_document_access: '',
          children_age_groups: [],
          dependents: [],
          onboarding_completed: false
        };
        
        const result = await signUp(email.trim(), password, userData);
        
        if (result.error) {
          setError(result.error);
          setLoading(false);
          return;
        }

        if (result.user) {
          // Sign-up succeeded. Supabase may require email confirmation.
          // If auto-confirmed, onAuthStateChange will set the user and
          // AppRouter will redirect. If email confirmation is required,
          // show a success message.
          if (result.user.confirmed_at || result.user.email_confirmed_at) {
            // Auto-confirmed — sign in immediately so AppRouter redirects
            const signInResult = await signIn(email.trim(), password);
            if (signInResult.error) {
              setError(signInResult.error);
            }
          } else {
            setSuccessMessage('Account created! Please check your email to confirm your account, then sign in.');
            setIsSignUp(false);
          }
          setLoading(false);
          return;
        }
      } else {
        const result = await signIn(email.trim(), password);
        if (result.error) {
          setError(result.error);
          setLoading(false);
          return;
        }
        // Success — AppRouter will redirect based on user/profile state
      }
      
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleBackToAuth = () => {
    setShowForgotPassword(false);
    setError(null);
  };

  const TabButton = ({ active, onClick, children }: { active: boolean, onClick: () => void, children: React.ReactNode }) => (
    <button
      onClick={onClick}
      className={`w-1/2 pb-2 text-center font-medium transition-colors duration-300
        ${active
          ? 'border-b-2 border-idecide-accent text-idecide-accent'
          : 'text-gray-500 hover:text-idecide-accent'
        }`}
    >
      {children}
    </button>
  );

  // Show forgot password component if that's the current view
  if (showForgotPassword) {
    return <ForgotPassword onBack={handleBackToAuth} />;
  }

  return (
    <div className="min-h-screen bg-idecide-light flex items-center justify-center p-4 animate-fade-in">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-idecide-dark">iDecide</h1>
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex mb-6 border-b border-gray-200">
            <TabButton active={!isSignUp} onClick={() => { setIsSignUp(false); setError(null); setSuccessMessage(null); }}>
              Sign In
            </TabButton>
            <TabButton active={isSignUp} onClick={() => { setIsSignUp(true); setError(null); setSuccessMessage(null); }}>
              Create Account
            </TabButton>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-idecide-secondary px-3 py-2 transition-shadow duration-300 focus:border-idecide-primary focus:outline-none focus:ring-2 focus:ring-idecide-primary/50"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-idecide-secondary px-3 py-2 transition-shadow duration-300 focus:border-idecide-primary focus:outline-none focus:ring-2 focus:ring-idecide-primary/50"
                required
              />
              {isSignUp && (
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 6 characters long
                </p>
              )}
            </div>

            {!isSignUp && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-idecide-accent hover:text-idecide-accent-hover font-medium"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {successMessage && (
              <div className="text-sm text-center p-3 rounded-md bg-green-50 text-green-700 border border-green-200">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="text-sm text-center p-3 rounded-md bg-red-50 text-red-700 border border-red-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-idecide-primary text-idecide-dark font-semibold rounded-md py-3 transition-colors duration-300 hover:bg-idecide-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}